import { readFileAsDataURL } from '@com/utils/file-reader'
import { getBlob } from '@ps/utils/base64-to-blob'
import { createImageComponent } from '@ps/bridge/qc/bin/image'
import { updateImageComponent } from '@ps/workbench/resource-manager/update-image-in-playable'
import store from '@ps/store'
import gameIframe from '@ps/bridge/iframe/iframe'
import { ElUploadInternalFileDetail } from 'element-ui/types/upload'
import qc from 'qc'
import i18n from '@ps/lang'
import { maxImageSize, AssetRoute } from '@ps/utils/config'
import validator, { ValidatorRule } from '@com/utils/file-validator'
import {
  convertJPEGtoJPG,
  getFileExtension,
  replaceJpegToJpg,
} from '@com/utils/file'
import { Message } from 'element-ui'
import { addDataURIPrefix } from '@com/utils/base64'
import { dataURItoBlob } from '@com/utils/data-uri'
import _ from 'lodash'
import { isImage, imageTypes } from './file'

export function validate(file: File, exceedTip?: string) {
  const fileName = file.name.toLowerCase()
  const rules = [
    {
      validate: ({ file }: ValidatorRule) => {
        if (!isImage(fileName)) {
          return {
            valid: false,
            message: `${i18n.t(
              'workbench.resourceManagers.imageUploader.uploadRule[0]',
            )} ${imageTypes.join('、')} ${i18n.t(
              'workbench.resourceManagers.imageUploader.uploadRule[1]',
            )}`,
          }
        }
        if (file.size > maxImageSize * 1024 * 1024) {
          return {
            valid: false,
            message:
              exceedTip ||
              `${i18n.t(
                'workbench.resourceManagers.imageUploader.uploadRule[2]',
              )}${maxImageSize}MB`,
          }
        }
        return {
          valid: true,
        }
      },
      file: file,
    },
  ]
  return validator(rules)
}

export async function addImageToProjectStore(
  file: ElUploadInternalFileDetail,
): Promise<{
  id: number
  file_url: string
}> {
  const fileName = file.name.toLowerCase()
  const exceedTip = `${i18n.t(
    'workbench.resourceManagers.imageUploader.uploadRule[3]',
  )}`.replace('xxx', `${maxImageSize}MB`)
  const { valid, errors } = validate(file.raw, exceedTip)
  if (!valid) {
    // @ts-ignore
    throw new Error(`${errors[0].message}`)
  }
  const rawFile =
    getFileExtension(fileName) === 'jpeg'
      ? await convertJPEGtoJPG(file.raw)
      : file.raw

  const res = await store.dispatch(
    'workspace/resourceManager/createComponent',
    {
      data: {
        file: rawFile,
        name: rawFile.name, // 保存原始名字到远端，保证从资源库新增资源时能够使用原有的名字
      },
      category: 'image',
    },
  )

  // 创建资源失败
  if (res.code !== 0) {
    throw new Error(res.msg)
  }

  // file 对象处理了 jpeg to jpg之后的文件
  return { ...res.data }
}

/**
 * 将图片添加到游戏中
 * @param file
 * @param isAdd 暂时 isAdd 不会为 false，因为拖拽上传组件暂时还没有拖拽物料替换节点资源的功能
 * @param isBg
 * @param activeNode
 * @param fileUrl
 * @param fileId
 * @param isAssetMode 是否是添加个人资产库资源，如果是添加个人资产库资源，不需要传 file
 */
export async function addImageToPlayable({
  file,
  isAdd,
  isBg,
  activeNode,
  fileUrl,
  fileId,
  isAssetMode = false,
  fileOriginName,
}: {
  file?: ElUploadInternalFileDetail
  addImageToScene?: boolean
  isAdd: boolean
  isBg: boolean
  activeNode: qc.Node | null
  fileUrl: string
  fileId: number
  isAssetMode?: boolean
  fileOriginName?: string
}): Promise<boolean> {
  let fileName = ''
  let dataURL = ''
  let assetFile: any = ''
  if (file) {
    fileName = file.name.toLowerCase()
  } else {
    fileName = fileOriginName!.toLowerCase()
  }

  // 必须等到游戏加载后才会添加到游戏中
  if (!gameIframe.iframe && !gameIframe.isGameReady) {
    return false
  }

  // 如果是个人资产库，需要先将资源添加到项目资源库中
  if (isAssetMode) {
    const res = await store.dispatch(
      'workspace/resourceManager/readComponent',
      {
        id: fileId,
        base64: 1,
        resSource: 'asset',
      },
    )
    if (res.code !== 0) {
      Message.error(res.msg)
      return false
    }
    dataURL = addDataURIPrefix(res.data.file_base64, getFileExtension(fileUrl))
    assetFile = dataURItoBlob(dataURL)
  }

  // 背景图片
  if (isBg) {
    if (isAssetMode) {
      await store.dispatch('workspace/globalConfig/updateBgImage', {
        file: assetFile,
        dataURL,
      })
    } else {
      await store.dispatch('workspace/globalConfig/updateBgImage', {
        file,
      })
    }

    const player = gameIframe.gamePlay.getScript('ps.GlobalConfigBg')
    player &&
      player.updateBgFilter({
        hue: 0,
        saturation: 0,
        luminance: 0,
        blur: 0,
      })
    await store.dispatch('workspace/editorConfig/updateTargetNodeConfig', {
      originalResource: fileUrl,
      resource: fileUrl,
      bg: true,
    })
    return true
  }
  // 普通资源
  if (isAdd) {
    if (isAssetMode) {
      await store.dispatch('workspace/game/addImageToScene', {
        file: assetFile,
        fileName,
        fileUrl: fileUrl,
      })
    } else if (file) {
      await store.dispatch('workspace/game/addImageToScene', {
        file: file.raw,
        fileName,
        fileUrl: fileUrl,
      })
    }
  } else {
    let component: any = ''
    const resourcePath = AssetRoute.GAME_ROOT
    if (file) {
      const result = await readFileAsDataURL(file.raw)
      const dataURL = result.target.result
      const newFile = getBlob(dataURL)
      // @ts-ignore
      newFile.name = file.name
      component = await createImageComponent({
        file: newFile as File,
        name: file.name,
        resourcePath,
      })
    } else if (isAssetMode) {
      component = await createImageComponent({
        file: assetFile,
        name: fileName,
        resourcePath,
      })
    }
    await updateImageComponent({
      // @ts-ignore
      data: component,
      addImageToConfig: true,
      resourcePath,
      isSetLibData: true,
    })
  }
  // 添加滤镜
  if (activeNode) {
    await store.dispatch('workspace/game/addFilter', {
      node: activeNode,
      filters: {
        hue: 0,
        saturation: 0,
        luminance: 0,
        blur: 0,
      },
    })
  }
  return true
}
